import { type KeyboardEvent, useCallback, useRef, useState } from "react";

/** Shared inline-rename state for gallery tree rows and the refine title. */
export function useInlineRename(
  entityId: string,
  currentName: string,
  onRename: (id: string, name: string) => void | Promise<void>,
) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const skipNextBlurRef = useRef(false);

  // Callback ref: focus and select the rename input when it mounts, so the
  // user can type immediately after double-click / F2.
  const focusOnMount = useCallback((node: HTMLInputElement | null) => {
    node?.focus();
    node?.select();
  }, []);

  const startEditing = () => {
    // Escape unmounts the focused input, and removing a focused element does
    // not reliably fire `blur` — so clear the guard here rather than relying on
    // the cancelled edit's blur to consume it.
    skipNextBlurRef.current = false;
    setDraftName(currentName);
    setEditingId(entityId);
  };

  const saveName = async () => {
    if (skipNextBlurRef.current) {
      skipNextBlurRef.current = false;
      return;
    }
    const nextName = draftName.trim();
    setEditingId(null);
    if (!nextName || nextName === currentName) {
      setDraftName("");
      return;
    }
    await onRename(entityId, nextName);
    setDraftName("");
  };

  const cancelEditing = () => {
    skipNextBlurRef.current = true;
    setEditingId(null);
    setDraftName("");
  };

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" && event.key !== "Escape") return;
    // The refine page binds a document-level Enter hotkey that fires inside
    // inputs; without this the rename would also kick off a refinement.
    event.stopPropagation();
    event.preventDefault();
    if (event.key === "Enter") {
      event.currentTarget.blur();
      return;
    }
    cancelEditing();
  };

  return {
    isEditing: editingId === entityId,
    draftName,
    setDraftName,
    startEditing,
    saveName,
    cancelEditing,
    handleNameKeyDown,
    focusOnMount,
  };
}
